/**
 * Chamadas HTTP das solicitações de compra do funcionário.
 *
 * Papel: aqui ficam as três chamadas de `/api/solicitacoes-de-compra` que a tela
 * usa — criar, listar as próprias e buscar a foto de uma delas.
 *
 * Como usar: a listagem não traz imagem; `temFoto` diz se há uma, e
 * `buscarFotoDaSolicitacao` é a única chamada que a traz, feita só para as linhas
 * que a marcam. Os schemas de resposta vêm de `@concierge/shared`, e a entrada de
 * `criarSolicitacao` é o `NovaSolicitacao` que o formulário já validou.
 */
import {
  type NovaSolicitacao,
  type SolicitacaoDoHistorico,
  idSchema,
  solicitacaoDoHistoricoSchema,
} from '@concierge/shared';
import { z } from 'zod';

import { clienteApi } from '@/shared/api';

/** O histórico inteiro do autor, sem envelope de paginação. */
const listaDeSolicitacoesSchema = z.array(solicitacaoDoHistoricoSchema);

/** A foto de uma solicitação; `foto` é nula quando ela não tem imagem. */
const fotoDaSolicitacaoSchema = z.object({
  id: idSchema,
  foto: z.string().nullable(),
});

/**
 * Lê o histórico do próprio usuário, da solicitação mais recente para a mais antiga.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns As solicitações, sem as imagens.
 */
export function listarSolicitacoes(sinal?: AbortSignal): Promise<SolicitacaoDoHistorico[]> {
  return clienteApi.get('/solicitacoes-de-compra', {
    schema: listaDeSolicitacoesSchema,
    sinal,
  });
}

/**
 * Envia uma solicitação nova.
 *
 * @param dados - Os cinco campos do formulário, já validados pelo schema do contrato.
 * @returns A linha do histórico como ficou gravada.
 */
export function criarSolicitacao(dados: NovaSolicitacao): Promise<SolicitacaoDoHistorico> {
  return clienteApi.post('/solicitacoes-de-compra', dados, {
    schema: solicitacaoDoHistoricoSchema,
  });
}

/**
 * Lê a imagem de uma solicitação do próprio usuário.
 *
 * Responde 404 para uma solicitação de outra pessoa, sem distinguir de uma que não
 * existe; a tela trata os dois casos como "sem imagem".
 *
 * @param id - Identificador da solicitação, o `id` da linha do histórico.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns A imagem em base64, ou `null` quando a solicitação não tem nenhuma.
 */
export function buscarFotoDaSolicitacao(
  id: string,
  sinal?: AbortSignal,
): Promise<{ id: string; foto: string | null }> {
  return clienteApi.get(`/solicitacoes-de-compra/${encodeURIComponent(id)}/foto`, {
    schema: fotoDaSolicitacaoSchema,
    sinal,
  });
}
